import React, {useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import IconButton from "../../../base/button/icon_button";
import {Edit, OpenInNew} from "@mui/icons-material";
import {Chip, SvgIcon} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import {ReactComponent as Person} from '../../../../../assets/images/sidebar/user.svg';
import {ReactComponent as DateTime} from '../../../../../assets/images/date-time-icon.svg';
import {Col, Row} from 'reactstrap';
import DateUtils from "../../../../../core/services/date-utils";
import {NotebookTagKeys} from "../../../../../core/models/static/routes/notebook-tags";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";

const NCRNoteCard = ({note, update, remove, isNotesPage = false}) => {
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    const ncrCode = useMemo(() => note?.tags?.find(e => e.key === NotebookTagKeys.ncrCode)?.value, [note])
    const ncrId = useMemo(() => note?.tags?.find(e => e.key === NotebookTagKeys.query)?.value, [note])

    /**
     * Removes the current note by calling its respective callback.
     * @return {Promise<void>}
     */
    const removeNote = async () => {
        setRemoving(true);
        await remove(note);
        if (!isMounted())
            return;
        setRemoving(false);
    }

    /**
     * Gets users confirmation before removing the current note entirely.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this note?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeNote(),
        );
    }

    return (
        <>
            <div className={'ncr-note-card'}>
                <div className={'header'}>
                    <div className={'d-flex w-100 justify-content-between'}>
                        <p className={'title flex-grow-1'}>
                            {note?.title ?? ''}
                        </p>
                        <div className={'d-flex align-items-start justify-content-end flex-shrink-1'}>
                            <IconButton
                                disabled={removing}
                                className={"mx-1"}
                                color={'paper.secondary'}
                                onPressed={() => update(note)}
                            >
                                <Edit className={"icon normal"}/>
                            </IconButton>
                            <IconButton
                                onPressed={getRemoveConfirmation}
                                disabled={removing}
                                color={'paper.error'}
                            >
                                <SvgIcon component={RemoveIcon} className='icon normal'/>
                            </IconButton>
                        </div>
                    </div>
                    {
                        isNotesPage && ncrCode &&
                        <Chip
                            className={'ml-3 mt-1 mb-1'}
                            label={`code: ${ncrCode ?? ''}`}
                            deleteIcon={<OpenInNew color={'primary'} sx={{width: 20, height: 20}}/>}
                            onDelete={() => window.open(routeFunctions.ncr.single(ncrId))}
                        />
                    }
                    <Row noGutters className={"ml-3 align-items-center"}>
                        <Col xs={'auto'}>
                            <SvgIcon component={Person} color={'secondary'} className={'icon small'}/>
                        </Col>
                        <Col xs className={"ml-2"}>
                            <p>
                                {note?.createdByName ?? ''}
                            </p>
                        </Col>
                    </Row>
                    {
                        note?.modifiedDateTime &&
                        <Row noGutters className={"ml-3 align-items-center"}>
                            <Col xs={'auto'}>
                                <SvgIcon component={DateTime} color={'secondary'} className={'icon small'}/>
                            </Col>
                            <Col xs className={"ml-2"}>
                                <p>
                                    {DateUtils.serverToLocal(note?.modifiedDateTime).format(DateUtils.dateFormat)}
                                </p>
                            </Col>
                        </Row>
                    }
                </div>
                <div
                    className={'description mt-2'}
                    dangerouslySetInnerHTML={{__html: note?.body ?? ""}}
                />
            </div>
        </>
    );
}


export default NCRNoteCard;
